import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import api from '../../services/api';
import { STAFFING_REQUIREMENT_STATUS } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const StaffingRequirementsPage = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    client_id: '',
    status: '',
    search: ''
  });

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get('/staffing/clients', { params: { limit: 1000 } });
        const list = response.data.data?.clients || [];
        setClients(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Failed to load clients:', error);
        setClients([]);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: pageSize,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value))
        };
        const response = await api.get('/staffing/requirements', { params });
        const data = response.data.data || {};
        setRequirements(Array.isArray(data.requirements) ? data.requirements : []);
        setPagination(data.pagination || null);
      } catch (error) {
        console.error('Failed to load requirements:', error);
        setRequirements([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRequirements();
  }, [page, pageSize, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>Requirements</h1>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-4">
                <label className="form-label small mb-1">Client</label>
                <select
                  name="client_id"
                  className="form-select"
                  value={filters.client_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All clients</option>
                  {clients.map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-lg-4">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All statuses</option>
                  {Object.values(STAFFING_REQUIREMENT_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-lg-4">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search by title"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table table-constrained mb-0 table-hover">
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <thead className="table-light border-light">
                  <tr>
                    <th>S.No.</th>
                    <th>Title</th>
                    <th>Client</th>
                    <th>Skills</th>
                    <th>Experience</th>
                    <th>Positions</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        No requirements found
                      </td>
                    </tr>
                  ) : (
                    requirements.map((requirement, index) => (
                      <tr key={requirement._id} className="align-middle">
                        <td className="text-nowrap">{(page - 1) * pageSize + index + 1}</td>
                        <td className="fw-medium cell-wrap">{requirement.title}</td>
                        <td className="cell-wrap">{requirement.client_id?.name || 'N/A'}</td>
                        <td className="cell-wrap">{Array.isArray(requirement.skills_required) ? requirement.skills_required.join(', ') : requirement.skills_required}</td>
                        <td className="cell-wrap">{requirement.experience_required}</td>
                        <td>{requirement.number_of_positions}</td>
                        <td><StatusBadge status={requirement.status} /></td>
                        <td>{formatDateTime(requirement.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/staffing/requirements/${requirement._id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination
                  pagination={pagination}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StaffingRequirementsPage;
